/**
 * ============================================================================
 * STORAGE SERVICE - Frontend
 * ============================================================================
 * 
 * Frontend service for file uploads and storage operations.
 * Handles file uploads, signed URL generation, and file management.
 */

import api from './api';

/**
 * Handle API errors
 */
const handleApiError = (error) => {
  if (error.response) {
    return error.response.data;
  }
  return { message: error.message || 'An error occurred' };
};

/**
 * Upload file
 * @param {File|Blob} file - File to upload
 * @param {Object} options - Upload options
 * @param {string} options.folder - Folder path
 * @param {Object} options.metadata - Additional metadata
 * @returns {Promise<Object>} Upload result with url, path, provider
 */
export const uploadFile = async (file, options = {}) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    if (options.folder) {
      formData.append('folder', options.folder);
    }
    
    if (options.metadata) {
      formData.append('metadata', JSON.stringify(options.metadata));
    }

    // api interceptor omits Content-Type for FormData so multipart boundary is set
    const response = await api.post('/storage/upload', formData);

    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Generate signed URL for file access
 * @param {string} filePath - File path
 * @param {Object} options - URL options
 * @param {number} options.expiresIn - Expiration time in seconds (default: 3600)
 * @returns {Promise<string>} Signed URL
 */
export const generateSignedUrl = async (filePath, options = {}) => {
  try {
    const response = await api.post('/storage/signed-url', {
      filePath,
      expiresIn: options.expiresIn || 3600,
    });

    return response.data.url;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Check if file exists
 * @param {string} filePath - File path
 * @returns {Promise<boolean>} True if file exists
 */
export const fileExists = async (filePath) => {
  try {
    const response = await api.get('/storage/check', {
      params: { filePath },
    });

    return response.data.exists;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get file metadata
 * @param {string} filePath - File path
 * @returns {Promise<Object>} File metadata
 */
export const getFileMetadata = async (filePath) => {
  try {
    const response = await api.get('/storage/metadata', {
      params: { filePath },
    });

    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Delete file
 * @param {string} filePath - File path
 * @returns {Promise<void>}
 */
export const deleteFile = async (filePath) => {
  try {
    await api.delete('/storage/delete', {
      params: { filePath },
    });
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Generate image preview URL
 * @param {string} filePath - Image file path
 * @param {Object} options - Preview options
 * @param {number} options.width - Preview width
 * @param {number} options.height - Preview height
 * @param {string} options.format - Image format (jpg, png, webp)
 * @returns {Promise<string>} Preview URL
 */
export const generateImagePreviewUrl = async (filePath, options = {}) => {
  try {
    const response = await api.post('/storage/image-preview', {
      filePath,
      width: options.width,
      height: options.height,
      format: options.format || 'jpg',
    });

    return response.data.url;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Generate PDF preview URL
 * @param {string} filePath - PDF file path
 * @param {Object} options - Preview options
 * @param {number} options.page - Page number (default: 1)
 * @param {number} options.width - Preview width
 * @returns {Promise<string>} Preview URL
 */
export const generatePDFPreviewUrl = async (filePath, options = {}) => {
  try {
    const response = await api.post('/storage/pdf-preview', {
      filePath,
      page: options.page || 1,
      width: options.width || 800,
    });

    return response.data.url;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Upload multiple files
 * @param {Array<File>} files - Array of files to upload
 * @param {Object} options - Upload options
 * @returns {Promise<Array<Object>>} Array of upload results
 */
export const uploadMultipleFiles = async (files, options = {}) => {
  try {
    const uploadPromises = files.map((file, index) =>
      uploadFile(file, {
        ...options,
        metadata: {
          ...options.metadata,
          index,
          total: files.length,
        },
      })
    );

    const results = await Promise.allSettled(uploadPromises);

    return results.map((result, index) => ({
      file: files[index],
      success: result.status === 'fulfilled',
      data: result.status === 'fulfilled' ? result.value : null,
      error: result.status === 'rejected' ? result.reason : null,
    }));
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * List user files
 * @param {Object} options - List options
 * @param {string} options.folder - Folder filter
 * @param {string} options.type - File type filter
 * @returns {Promise<Array>} Array of file objects
 */
export const listFiles = async (options = {}) => {
  try {
    const response = await api.get('/storage/files', {
      params: options,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get storage quota
 * @returns {Promise<Object>} Quota information (used, limit, remaining)
 */
export const getStorageQuota = async () => {
  try {
    const response = await api.get('/storage/quota');
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export default {
  uploadFile,
  uploadMultipleFiles,
  generateSignedUrl,
  fileExists,
  getFileMetadata,
  deleteFile,
  generateImagePreviewUrl,
  generatePDFPreviewUrl,
  listFiles,
  getStorageQuota,
};
